import { currentUserQuery } from "@common/api/users"
import { useUpdateWorkPackagePhaseMutation } from "@common/api/work-package-phases"
import { workPackageTasksQuery } from "@common/api/work-packages"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { PhaseNameDialog } from "@/components/phase/phase-name-dialog"
import useCustomToast from "@/hooks/use-custom-toast"

export const Route = createFileRoute(
  "/_layout/work-orders/$workOrderId/phases/$phaseId/rename",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "work_package:update")) {
      throw redirect({
        to: "/work-orders/$workOrderId",
        params: { workOrderId: params.workOrderId },
        search,
      })
    }
  },
  component: RenamePhaseDialog,
})

function RenamePhaseDialog(): React.ReactElement {
  const { workOrderId, phaseId } = Route.useParams()
  const { data: workOrder } = useSuspenseQuery(
    workPackageTasksQuery({ id: workOrderId }),
  )
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useUpdateWorkPackagePhaseMutation()
  const phase = workOrder.active_phases.find(({ id }) => id === phaseId)
  const close = () =>
    navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: true,
    })

  return (
    <PhaseNameDialog
      title="Rename phase"
      description="The new name shows wherever this phase groups the work order's tasks."
      name={phase?.name ?? ""}
      action="Rename"
      pending={mutation.isPending}
      onSave={(name) =>
        mutation.mutate(
          {
            work_package_id: workOrderId,
            phase_id: phaseId,
            workPackagePhaseUpdate: { name },
          },
          {
            onSuccess: close,
            onError: (error) => handleError(error, showToast),
          },
        )
      }
      onCancel={close}
    />
  )
}
